import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AmountField, parseAmount } from "@/components/amount-field";
import { LoadFields, buildFieldState, collectFieldPatch } from "@/components/load-fields";
import { describeError, useLoad, usePatchLoad, type LoadPatchPayload } from "@/lib/api";

// The post-capture pricing step. The load already exists (capture created
// it, prefilled from the parser, with amount NULL); this screen exists to
// get the pay onto it. Amount is required here — that is the difference
// between this and the detail screen, where amount is merely editable.
export function Review() {
  const [, params] = useRoute<{ id: string }>("/l/:id/review");
  const [, navigate] = useLocation();
  const id = params ? Number(params.id) : null;
  const valid = id !== null && Number.isInteger(id) && id > 0;

  const { data, isPending, isError, error } = useLoad(valid ? id : null);
  const patch = usePatchLoad(id ?? 0);

  const [amount, setAmount] = useState("");
  const [touched, setTouched] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const initedFor = useRef<number | null>(null);

  useEffect(() => {
    const load = data?.load;
    if (load && initedFor.current !== load.id) {
      const init = buildFieldState(load);
      setFields(init);
      setBaseline(init);
      setAmount(load.amount ?? "");
      initedFor.current = load.id;
    }
  }, [data]);

  const parsedAmount = parseAmount(amount);
  const amountValid = parsedAmount !== null;

  function onSave() {
    // Guarded as well as disabled: the amount is the whole point of the
    // module, so a malformed one must never reach the server.
    if (id == null || !amountValid || data?.load === undefined) {
      setTouched(true);
      return;
    }

    const payload: LoadPatchPayload = {
      amount: parsedAmount,
      ...collectFieldPatch(fields, baseline),
    };

    patch.mutate(payload, {
      onSuccess: () => {
        toast.success("Load saved");
        // Back to the list, where the row now shows the amount.
        navigate("/");
      },
      // Errors render inline next to the button rather than as a toast:
      // the driver is looking at the amount they just typed, and a save
      // failure is about that, not a passing notification.
    });
  }

  return (
    <div className="min-h-dvh max-w-2xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center gap-3">
        <Link href="/">
          <button type="button" aria-label="Back" className="p-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-5 w-5" />
          </button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Add your pay</h1>
          <p className="text-sm text-muted-foreground">Check the details, then enter the amount</p>
        </div>
      </header>

      {!valid && <Card className="p-4 text-sm text-destructive">Invalid load id.</Card>}

      {valid && isPending && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {valid && isError && (
        <Card className="p-4 text-sm text-destructive" role="alert">
          Couldn&apos;t open this load: {describeError(error)}
        </Card>
      )}

      {valid && data && (
        <>
          <Card className="p-4">
            <AmountField
              value={amount}
              onChange={(v) => setAmount(v)}
              onBlur={() => setTouched(true)}
              showError={touched && !amountValid}
              required
            />
          </Card>

          <Card className="p-4 space-y-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              From the ticket
            </h2>
            <LoadFields
              load={data.load}
              values={fields}
              baseline={baseline}
              onChange={(name, v) => setFields((s) => ({ ...s, [name]: v }))}
            />
          </Card>

          {patch.isError && (
            <Card className="p-4 text-sm text-destructive" role="alert">
              Couldn&apos;t save load: {describeError(patch.error)}
            </Card>
          )}

          <Button
            size="lg"
            className="w-full h-14 text-base"
            onClick={onSave}
            disabled={!amountValid || patch.isPending}
          >
            {patch.isPending ? "Saving…" : "Save load"}
          </Button>
        </>
      )}
    </div>
  );
}
